import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import SessionCard from "@/components/SessionCard";
import BookingModal from "@/components/BookingModal";
import PhotoMarquee from "@/components/PhotoMarquee";
import { setLang } from "@/i18n";
import { Sparkles, Ticket, Search, X as XIcon, Globe, MapPin, CalendarDays } from "lucide-react";
import { DrumIcon, HutIcon, BaobabIcon } from "@/components/AfricanIcons";
import MicButton from "@/components/MicButton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["all", "workshop", "training", "side_event"];

const HOME_LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
];

const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
const DOW = ["SUN","MON","TUE","WED","THU","FRI","SAT"];

export default function BrowsePage() {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = React.useState([]);
  const [allSessions, setAllSessions] = React.useState([]); // used to build the dates row
  const [day, setDay] = React.useState(null); // null = all dates
  const [type, setType] = React.useState("all");
  const [loading, setLoading] = React.useState(true);
  const [modal, setModal] = React.useState(null);
  const [query, setQuery] = React.useState("");

  // Load all sessions once to build the dynamic date scroller
  React.useEffect(() => {
    axios.get(`${API}/sessions`).then((res) => {
      setAllSessions(res.data);
      // Auto-select the first date on first load
      if (res.data.length && day === null) {
        const first = [...new Set(res.data.map((s) => s.day))].sort((a, b) => a - b)[0];
        setDay(first);
      }
    }).catch(() => toast.error("Failed to load agenda"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load filtered list whenever day/type changes
  const load = React.useCallback(async () => {
    if (day === null) return;
    setLoading(true);
    try {
      const params = { day };
      if (type !== "all") params.type = type;
      const res = await axios.get(`${API}/sessions`, { params });
      setSessions(res.data);
    } catch (e) { toast.error("Failed to load sessions"); }
    finally { setLoading(false); }
  }, [day, type]);

  React.useEffect(() => { load(); }, [load]);

  // Derive side-event count for the hero stat tile
  const sideEventCount = React.useMemo(
    () => allSessions.filter((s) => s.type === "side_event").length,
    [allSessions]
  );

  // Derive the date list from allSessions
  const dateList = React.useMemo(() => {
    const map = new Map();
    for (const s of allSessions) {
      if (!map.has(s.day)) map.set(s.day, s.starts_at);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([d, iso]) => {
      const dt = new Date(iso);
      return {
        day: d,
        dow: DOW[dt.getDay()],
        num: String(dt.getDate()).padStart(2, "0"),
        month: MONTHS[dt.getMonth()],
      };
    });
  }, [allSessions]);

  // Derive the unique organizer list from allSessions (with counts) sorted by frequency
  const orgList = React.useMemo(() => {
    const counts = new Map();
    for (const s of allSessions) {
      const o = (s.organization || "").trim();
      if (!o) continue;
      counts.set(o, (counts.get(o) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [allSessions]);

  // Short label for a long org name (first meaningful token / acronym)
  const orgShort = (name) => {
    const m = name.match(/\(([^)]+)\)/); // pick text inside parentheses first: WACOL, RHNK, FPRA…
    if (m) return m[1];
    const t = name.split(/[,&·\/]/)[0].trim();
    return t.length > 26 ? t.slice(0, 24).trim() + "…" : t;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Language row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="home-lang-row">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">
          <Globe className="w-3.5 h-3.5" /> Language
        </span>
        {HOME_LANGS.map((l) => (
          <button
            key={l.code}
            data-testid={`home-lang-${l.code}`}
            data-active={i18n.language === l.code}
            onClick={() => setLang(l.code)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--line-strong)] px-3 py-1 text-xs font-semibold text-[color:var(--ink-2)] hover:border-[color:var(--forest)] hover:text-[color:var(--forest)] data-[active=true]:bg-[color:var(--forest)] data-[active=true]:border-[color:var(--forest)] data-[active=true]:text-white transition-all"
          >
            <span className="text-sm leading-none">{l.flag}</span>
            {l.label}
          </button>
        ))}
      </div>

      {/* Hero banner */}
      <section className="hero-banner">
        <div className="relative z-10 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
                <Ticket className="w-4 h-4 text-[color:var(--amber-soft)]" />
              </div>
              <div className="font-serif-diplo text-white tracking-tight text-2xl sm:text-[28px] leading-none">
                <span className="text-white">EVENT</span>{" "}
                <span className="text-[color:var(--amber-soft)]">HUB</span>
              </div>
            </div>
            <div className="text-[11px] font-mono-diplo tracking-widest uppercase text-white/80">
              Discover · Choose · Reserve · Attend
            </div>
          </div>
          <a
            href="https://pakebbagaye.com.free/"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-agency-badge"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 px-2.5 py-1 text-[10px] text-white transition-colors"
          >
            <span className="w-4 h-4 rounded bg-[color:var(--amber)] text-[8px] font-bold text-white flex items-center justify-center">PKG</span>
            Nexora Digital
          </a>
        </div>

        {/* Hero stat strip */}
        <div className="relative z-10 mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:max-w-2xl" data-testid="hero-stats">
          <div className="hero-stat">
            <span className="eyebrow">NGO Forum</span>
            <div className="headline">
              <span className="num">9</span>
              <span className="num-unit">days</span>
            </div>
            <span className="lbl">Oct 7 – 15, 2026</span>
          </div>
          <a
            data-testid="hero-venue-map"
            href="https://www.google.com/maps/search/?api=1&query=Pullman+Dakar+Teranga+Hotel+Dakar+Senegal"
            target="_blank"
            rel="noreferrer"
            className="hero-stat group"
            title="Open Pullman Hotel Dakar in Google Maps"
          >
            <span className="eyebrow flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-[color:var(--amber-soft)]" /> Main Venue
            </span>
            <span className="venue-name">Pullman Hotel · Dakar</span>
            <span className="lbl inline-flex items-center gap-1">
              Tap to open in Google Maps
              <span className="font-mono text-[9px] uppercase tracking-widest opacity-80 group-hover:opacity-100 transition-opacity">↗</span>
            </span>
          </a>
          <div className="hero-stat">
            <span className="eyebrow">Programme</span>
            <div className="headline">
              <span className="num" data-testid="hero-side-event-count">{sideEventCount}</span>
              <span className="num-unit">side events</span>
            </div>
            <span className="lbl">Workshops · Trainings · Networking</span>
          </div>
        </div>
      </section>

      {/* Featured — 89th ACHPR NGO Forum */}
      <section className="mt-4 diplomatic-card overflow-hidden animate-fade-up" data-testid="featured-forum">
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="tag-mono text-[color:var(--forest)]">🇸🇳 {t("featured.badge")}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--amber-soft)] text-[color:var(--amber-dark)]">
              {t("featured.dates")}
            </span>
            <span data-testid="featured-session-tag" className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--forest-soft)] text-[color:var(--forest-dark)] border border-[color:var(--forest)]/20">
              {t("featured.session_tag")}
            </span>
          </div>
          <h1 data-testid="featured-forum-title" className="font-serif-diplo text-xl sm:text-2xl leading-tight text-[color:var(--ink)]">
            {t("featured.title")}<sup className="text-sm">{i18n.language === "pt" ? "" : (i18n.language === "fr" ? "e" : (i18n.language === "ar" ? "" : "th"))}</sup>{t("featured.title_suffix")}
          </h1>
          <p data-testid="featured-subtitle" className="text-sm text-[color:var(--ink-2)] mt-1 font-semibold">
            {t("featured.subtitle")}
          </p>

          {/* Theme of the Year 2026 — feature callout */}
          <div data-testid="featured-theme-2026" className="mt-4 relative overflow-hidden rounded-xl border border-[color:var(--amber)]/40 bg-gradient-to-br from-[color:var(--amber-soft)] via-white to-[color:var(--forest-soft)] p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[9px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-[color:var(--amber)] text-white">{t("featured.theme_label")}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">{t("featured.theme_scope")}</span>
            </div>
            <blockquote className="font-serif-diplo text-base sm:text-lg leading-snug text-[color:var(--forest-dark)] italic">
              {t("featured.theme_quote")}
            </blockquote>
          </div>

          <p className="text-sm text-[color:var(--ink-2)] mt-4 leading-relaxed flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[color:var(--forest)]" />
            {t("featured.two_venues")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="venue-map-links">
            <a
              data-testid="map-link-pullman"
              href="https://www.google.com/maps/search/?api=1&query=Pullman+Dakar+Teranga+Hotel+Dakar+Senegal"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--line-strong)] bg-white px-3 py-1.5 text-xs font-semibold text-[color:var(--ink)] hover:border-[color:var(--forest)] hover:text-[color:var(--forest)] transition-colors"
            >
              <MapPin className="w-3.5 h-3.5 text-[color:var(--forest)]" />
              Pullman Hotel
              <span className="text-[10px] font-mono text-[color:var(--ink-3)] uppercase tracking-widest">Map</span>
            </a>
            <a
              data-testid="map-link-kingfadh"
              href="https://www.google.com/maps/search/?api=1&query=King+Fahd+Palace+Hotel+Dakar+Senegal"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--line-strong)] bg-white px-3 py-1.5 text-xs font-semibold text-[color:var(--ink)] hover:border-[color:var(--amber)] hover:text-[color:var(--amber-dark)] transition-colors"
            >
              <MapPin className="w-3.5 h-3.5 text-[color:var(--amber)]" />
              King Fadh Hotel
              <span className="text-[10px] font-mono text-[color:var(--ink-3)] uppercase tracking-widest">Map</span>
            </a>
          </div>
          <button
            data-testid="hero-cta-bulk"
            onClick={() => setModal({ mode: "bulk" })}
            className="btn-reserve btn-reserve-green mt-5 inline-flex items-center justify-center gap-2"
          >
            <Ticket className="w-4 h-4" /> {t("featured.reserve_multiple")}
          </button>
        </div>
      </section>

      {/* Photo marquee */}
      <PhotoMarquee />

      {/* Explore Events - category quick-cards */}
      <section className="mt-8">
        <div className="mb-3">
          <span className="section-eyebrow">Categories</span>
          <h2 className="font-serif-diplo text-xl sm:text-2xl mt-1 text-[color:var(--ink)]">Explore Events</h2>
          <div className="gold-underline mt-2" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            data-testid="cat-workshop"
            onClick={() => setType("workshop")}
            className={`cat-card cat-card-green ${type === "workshop" ? "ring-2 ring-offset-2 ring-[color:var(--forest)]" : ""}`}
          >
            <span className="cat-icon-tile"><DrumIcon className="w-5 h-5 text-white" /></span>
            <div className="relative z-10">
              <div className="text-lg font-bold uppercase tracking-wide">Workshops</div>
              <div className="text-[11px] opacity-85 mt-0.5 font-mono uppercase tracking-widest">Training · Learning · Skills</div>
            </div>
          </button>
          <button
            data-testid="cat-training"
            onClick={() => setType("training")}
            className={`cat-card cat-card-blue ${type === "training" ? "ring-2 ring-offset-2 ring-[#2A6EB8]" : ""}`}
          >
            <span className="cat-icon-tile"><BaobabIcon className="w-5 h-5 text-white" /></span>
            <div className="relative z-10">
              <div className="text-lg font-bold uppercase tracking-wide">Trainings</div>
              <div className="text-[11px] opacity-90 mt-0.5 font-mono uppercase tracking-widest">Methodology · Certification</div>
            </div>
          </button>
          <button
            data-testid="cat-side_event"
            onClick={() => setType("side_event")}
            className={`cat-card cat-card-amber col-span-2 sm:col-span-1 ${type === "side_event" ? "ring-2 ring-offset-2 ring-[color:var(--amber)]" : ""}`}
          >
            <span className="cat-icon-tile"><HutIcon className="w-5 h-5 text-white" /></span>
            <div className="relative z-10">
              <div className="text-lg font-bold uppercase tracking-wide">Side Events</div>
              <div className="text-[11px] opacity-90 mt-0.5 font-mono uppercase tracking-widest">Networking · Community</div>
            </div>
          </button>
        </div>
      </section>

      {/* Upcoming Events */}
      <section id="sessions" className="mt-8">
        <div className="mb-3">
          <span className="section-eyebrow">Agenda</span>
          <h2 className="font-serif-diplo text-xl sm:text-2xl mt-1 text-[color:var(--ink)]">Upcoming Events</h2>
          <div className="gold-underline mt-2" />
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {TYPES.map((tp) => (
            <button
              key={tp}
              data-testid={`type-filter-${tp}`}
              data-active={type === tp}
              onClick={() => setType(tp)}
              className="filter-pill filter-pill-sm"
            >
              {t(`filters.${tp}`)}
            </button>
          ))}
        </div>

        {/* Date scroller — dynamic dates from seeded sessions */}
        <div className="flex items-center gap-1.5 mb-4 overflow-x-auto no-scrollbar pb-1" data-testid="date-scroller">
          {dateList.map((d) => (
            <button
              key={d.day}
              data-testid={`date-tab-${d.day}`}
              data-active={day === d.day}
              onClick={() => setDay(d.day)}
              className="flex-shrink-0 rounded-xl border px-3 py-2 text-center transition-all bg-white border-[color:var(--line-strong)] hover:border-[color:var(--forest)] data-[active=true]:bg-[color:var(--forest)] data-[active=true]:border-[color:var(--forest)] data-[active=true]:text-white"
            >
              <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">{d.dow}</div>
              <div className="font-serif-diplo text-lg leading-none mt-0.5">{d.num}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">{d.month}</div>
            </button>
          ))}
        </div>

        {/* Voice + text search bar */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[color:var(--ink-3)] absolute start-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="session-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sessions or tap the mic…"
              className="w-full ps-9 pe-9 py-2.5 rounded-full border text-sm"
            />
            {query && (
              <button
                data-testid="session-search-clear"
                onClick={() => setQuery("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)] hover:text-[color:var(--forest)]"
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          <MicButton
            testId="session-voice-search"
            language={i18n.language === "ar" ? "ar" : i18n.language === "fr" ? "fr" : i18n.language === "pt" ? "pt" : "en"}
            onTranscript={(text) => {
              if (text) { setQuery(text); toast.success(`🎙 "${text}"`); }
            }}
          />
        </div>

        {(() => {
          const q = query.trim().toLowerCase();
          const lng = i18n.language || "en";
          const shown = q
            ? sessions.filter((s) => {
                const title = (s.title?.[lng] || s.title?.en || "").toLowerCase();
                const room = (s.room || "").toLowerCase();
                const fac = (s.facilitator || s.focal_person || "").toLowerCase();
                const orgn = (s.organization || "").toLowerCase();
                const topic = (s.topic || "").toLowerCase();
                return title.includes(q) || room.includes(q) || fac.includes(q) || orgn.includes(q) || topic.includes(q);
              })
            : sessions;
          if (loading) return (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-40 rounded-2xl bg-white/60 animate-pulse" />)}
            </div>
          );
          if (shown.length === 0) return (
            <div className="py-16 text-center text-[color:var(--ink-3)] font-mono-diplo text-xs" data-testid="empty-sessions">
              {q ? `No match for "${query}"` : type === "workshop" ? "No workshops on this date." : "No sessions for this filter."}
            </div>
          );
          return (
            <div className="space-y-3 stagger">
              {shown.map((s) => (
                <SessionCard key={s.id} session={s} onReserve={(sess) => setModal({ mode: "single", session: sess })} />
              ))}
            </div>
          );
        })()}
      </section>

      {modal && (
        <BookingModal
          session={modal.session}
          mode={modal.mode}
          onClose={() => setModal(null)}
          onConfirmed={() => load()}
        />
      )}
    </div>
  );
}
